import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { reportError } from '../utils/reportError';

// Finance · Module 3 — Cash-Flow Forecast (3 × 30-day periods).
const inr = (v) => (v < 0 ? '-₹' : '₹') + Math.abs(Math.round(Number(v) || 0)).toLocaleString('en-IN');
const input = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lab = { fontSize: 10, color: '#888', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase' };

export default function CashFlowForecast() {
  const [companies, setCompanies] = useState([]); const [canSwitch, setCanSwitch] = useState(false); const [company, setCompany] = useState('');
  const [fc, setFc] = useState(null); const [manual, setManual] = useState([]);
  const [mform, setMform] = useState({ flow_date: '', direction: 'out', amount: '', description: '' });
  const H = useCallback(() => ({ headers: { 'X-Company-Id': company } }), [company]);

  useEffect(() => {
    api.get('/finance/daybook/companies').then(r => { setCompanies(r.data.data || []); setCanSwitch(r.data.can_switch); setCompany(r.data.active || (r.data.data[0] && r.data.data[0].id) || ''); }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!company) return;
    api.get('/finance/forecast', H()).then(r => setFc(r.data.data)).catch(() => setFc(null));
    api.get('/finance/forecast/manual', H()).then(r => setManual(r.data.data || [])).catch(() => {});
  }, [company, H]);
  useEffect(() => { load(); }, [load]);

  const savePos = (patch) => api.put('/finance/forecast/position', patch, H()).then(() => load()).catch(reportError('save the opening position'));
  const addManual = (e) => {
    e.preventDefault();
    if (!mform.flow_date || !mform.amount) return;
    api.post('/finance/forecast/manual', { ...mform, amount: Number(mform.amount) }, H())
      .then(() => { setMform({ flow_date: '', direction: 'out', amount: '', description: '' }); load(); }).catch(reportError('add this cash flow'));
  };
  const delManual = (id) => api.delete('/finance/forecast/manual/' + id, H()).then(load).catch(reportError('delete this cash flow'));

  const th = { padding: '9px 10px', textAlign: 'right', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap', textAlign: 'right' };
  const pos = fc?.position || {};

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>Cash-Flow Forecast</div>
          <div style={{ fontSize: 11, color: '#888' }}>Next 90 days · receivables in − (payables + payroll + day-book run-rate + manual) out</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>Company</span>
          <select value={company} disabled={!canSwitch} onChange={e => setCompany(e.target.value)} style={{ ...input, fontWeight: 700, color: '#3B3A8F', background: canSwitch ? '#EEEDF8' : '#f5f5f5' }}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {/* Opening position + payroll */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', background: '#F7F6F3', border: '1px solid #E8E7E7', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
        {/* Cash/bank are the DERIVED live position from the Day Book reconciliation — no longer
            hand-typed here (mig 174, ruling 4). Set them by recording a reconciliation. */}
        <div><label style={lab}>Cash in hand</label><div style={{ ...input, width: 130, background: '#efeef6', display: 'flex', alignItems: 'center', fontWeight: 700, color: '#854F0B' }}>{pos.position_established ? inr(pos.cash_balance) : '—'}</div></div>
        <div><label style={lab}>Bank balance</label><div style={{ ...input, width: 130, background: '#efeef6', display: 'flex', alignItems: 'center', fontWeight: 700, color: '#0E7490' }}>{pos.position_established ? inr(pos.bank_balance) : '—'}</div></div>
        <div><label style={lab}>Monthly payroll est.</label><input type="number" key={'p' + pos.monthly_payroll_estimate} defaultValue={pos.monthly_payroll_estimate} onBlur={e => savePos({ monthly_payroll_estimate: Number(e.target.value) })} style={{ ...input, width: 140 }} /></div>
        <div style={{ paddingBottom: 7 }}>
          <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>Opening</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#3B3A8F' }}>{inr(fc?.opening || 0)}</div>
        </div>
        <div style={{ paddingBottom: 7, fontSize: 11, color: '#999' }}>Day-book run-rate: <b>{inr(fc?.run_rate_monthly || 0)}</b>/mo (auto)</div>
      </div>
      {pos && pos.position_established === false && (
        <div style={{ background: '#FFF7E6', border: '1px solid #E0A800', color: '#7A5A00', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 12 }}>
          Cash/bank opening not yet established — record an opening reconciliation in <b>Day Book → Reconciliation</b> to drive the forecast from a true position.
        </div>
      )}

      {fc?.liquidity_warning && (
        <div style={{ background: '#FDECEC', border: '1px solid #f5b5b5', color: '#A32D2D', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 700 }}>
          ⚠️ Liquidity warning — projected balance goes negative within 90 days. Plan collections or defer outflows.
        </div>
      )}

      {/* Projection table */}
      <div style={{ overflowX: 'auto', border: '1px solid #E8E7E7', borderRadius: 8, marginBottom: 18 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            <th style={{ ...th, textAlign: 'left' }}>Period</th><th style={th}>Opening</th>
            <th style={{ ...th, color: '#166534' }}>+ Receivables</th><th style={{ ...th, color: '#166534' }}>+ Manual in</th>
            <th style={{ ...th, color: '#A32D2D' }}>− Payables</th><th style={{ ...th, color: '#A32D2D' }}>− Payroll</th>
            <th style={{ ...th, color: '#A32D2D' }}>− Run-rate</th><th style={{ ...th, color: '#A32D2D' }}>− Manual out</th>
            <th style={th}>Net</th><th style={th}>Closing</th>
          </tr></thead>
          <tbody>
            {!fc ? <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</td></tr>
            : fc.periods.map((p, i) => (
              <tr key={i} style={{ background: p.negative ? '#FFF5F5' : 'transparent' }}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{p.label}</td>
                <td style={td}>{inr(p.opening)}</td>
                <td style={{ ...td, color: '#166534' }}>{p.in_receivables ? inr(p.in_receivables) : '—'}</td>
                <td style={{ ...td, color: '#166534' }}>{p.in_manual ? inr(p.in_manual) : '—'}</td>
                <td style={{ ...td, color: '#A32D2D' }}>{p.out_payables ? inr(p.out_payables) : '—'}</td>
                <td style={{ ...td, color: '#A32D2D' }}>{p.out_payroll ? inr(p.out_payroll) : '—'}</td>
                <td style={{ ...td, color: '#A32D2D' }}>{p.out_runrate ? inr(p.out_runrate) : '—'}</td>
                <td style={{ ...td, color: '#A32D2D' }}>{p.out_manual ? inr(p.out_manual) : '—'}</td>
                <td style={{ ...td, fontWeight: 700, color: p.net < 0 ? '#A32D2D' : '#166534' }}>{inr(p.net)}</td>
                <td style={{ ...td, fontWeight: 800, color: p.negative ? '#A32D2D' : '#166534' }}>{inr(p.closing)}{p.negative ? ' 🔴' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Manual expected flows */}
      <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>Manual expected flows</div>
      <form onSubmit={addManual} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
        <div><label style={lab}>Date</label><input type="date" value={mform.flow_date} onChange={e => setMform({ ...mform, flow_date: e.target.value })} style={{ ...input, width: 150 }} required /></div>
        <div><label style={lab}>Direction</label><select value={mform.direction} onChange={e => setMform({ ...mform, direction: e.target.value })} style={{ ...input, width: 120 }}><option value="out">Outflow</option><option value="in">Inflow</option></select></div>
        <div><label style={lab}>Amount</label><input type="number" min="0" value={mform.amount} onChange={e => setMform({ ...mform, amount: e.target.value })} style={{ ...input, width: 130 }} required /></div>
        <div style={{ flex: 1, minWidth: 160 }}><label style={lab}>Description</label><input value={mform.description} onChange={e => setMform({ ...mform, description: e.target.value })} style={{ ...input, width: '100%' }} placeholder="e.g. machine advance" /></div>
        <button type="submit" style={{ ...input, background: '#3B3A8F', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }}>+ Add</button>
      </form>
      <div style={{ border: '1px solid #E8E7E7', borderRadius: 8, overflow: 'hidden' }}>
        {manual.length === 0 ? <div style={{ padding: 14, color: '#aaa', fontSize: 12 }}>No manual flows in the next 90 days.</div>
          : manual.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid #f5f5f5', fontSize: 12 }}>
              <span style={{ width: 90, color: '#666' }}>{String(m.flow_date).slice(0, 10)}</span>
              <span style={{ width: 70, fontWeight: 700, color: m.direction === 'in' ? '#166534' : '#A32D2D' }}>{m.direction === 'in' ? 'Inflow' : 'Outflow'}</span>
              <span style={{ width: 110, fontWeight: 700 }}>{inr(m.amount)}</span>
              <span style={{ flex: 1, color: '#555' }}>{m.description || '—'}</span>
              <button onClick={() => delManual(m.id)} style={{ border: 'none', background: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 12 }}>Delete</button>
            </div>
          ))}
      </div>
    </div>
  );
}
